/**
 * Config Registry Filters Component
 * Slide-out drawer with filter options
 */

import React, { useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Divider,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useGlobalConfig } from 'context/GlobalContext';
import { ANALYTICS_EVENTS } from 'framework/analytics/AnalyticsEvents';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectConfigRegistryFilters,
  selectUISchema,
  selectConfigTypes,
  selectIsFilterPanelOpen,
  setFilters,
  resetFilters,
  setFilterPanelOpen,
} from '../store/configRegistryUISlice';
import {
  BglPrimarySmallButton,
  BglCloseOutlinedSmallButton,
} from 'components/button/BglButtons';
import { ConfigVerificationStatus, ConfigRegistryFilters as FilterType } from '../types/configRegistryTypes';
import { SearchableSelect } from 'components/common/SearchableSelect';

const DRAWER_WIDTH = 320;

const STATUS_OPTIONS: { value: ConfigVerificationStatus | ''; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'TODO', label: 'To Do' },
  { value: 'READY_FOR_QA', label: 'Ready for QA' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

const RESTRICTED_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

export const ConfigRegistryFiltersDrawer = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { rc } = useGlobalConfig();

  const filters = useSelector(selectConfigRegistryFilters);
  const uiSchema = useSelector(selectUISchema);
  const configTypes = useSelector(selectConfigTypes);
  const isOpen = useSelector(selectIsFilterPanelOpen);

  // Get lookup options from UI schema
  const getLookupOptions = useCallback((typeCode: string, parentValue?: string) => {
    if (!uiSchema?.lookupTypes) return [];
    const lookupType = uiSchema.lookupTypes.find(lt => lt.code === typeCode);
    if (!lookupType?.lookups) return [];

    // Filter by parent value if provided (for dependent dropdowns)
    if (parentValue && lookupType.dependsOn) {
      return lookupType.lookups.filter(l => l.parentValue === parentValue);
    }
    return lookupType.lookups;
  }, [uiSchema]);

  const handleFilterChange = (field: keyof FilterType, value: string) => {
    const updates: Partial<FilterType> = { [field]: value };

    // Clear dependent filters when parent changes
    // Note: SECTION is independent of COMPONENT (not a child)
    // Only FILE_TYPE depends on CATEGORIZATION
    if (field === 'classification') {
      updates.fileType = '';
    }

    dispatch(setFilters(updates));
  };

  const handleClose = () => dispatch(setFilterPanelOpen(false));
  const handleReset = () => dispatch(resetFilters());

  // "Apply" is the discrete commit point for the live-updating filter fields.
  const handleApply = () => {
    // Count active (non-empty / non-default) filters as a scalar for analytics.
    const activeFilterCount = [
      filters.configType,
      filters.verificationStatus || filters.approvalStatus,
      filters.component,
      filters.section,
      filters.classification,
      filters.fileType,
      filters.restricted && filters.restricted !== 'all' ? filters.restricted : '',
    ].filter(Boolean).length;

    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.FILTER_APPLIED, {
      filter_count: activeFilterCount,
    });

    // Search field is a live filter, but Apply is its discrete submit point.
    if (filters.search && filters.search.trim()) {
      rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.SEARCH_EXECUTED);
    }

    handleClose();
  };

  const componentOptions = getLookupOptions('COMPONENT');
  const sectionOptions = getLookupOptions('SECTION'); // SECTION is independent, not a child of COMPONENT
  const classificationOptions = getLookupOptions('CATEGORIZATION');
  const fileTypeOptions = getLookupOptions('FILE_TYPE', filters.classification);

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          bgcolor: 'background.paper',
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Filters</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Filter Fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, overflowY: 'auto' }}>
          {/* Search */}
          <TextField
            label="Search"
            placeholder="Search by key or name..."
            size="small"
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            fullWidth
          />

          {/* Config Type */}
          <SearchableSelect
            size="small"
            fullWidth
            label="Config Type"
            value={filters.configType || ''}
            onChange={(v) => handleFilterChange('configType', v)}
            options={[{ value: '', label: 'All Types' }, ...configTypes.map((type) => ({ value: type, label: type }))]}
          />

          {/* Status */}
          <SearchableSelect
            size="small"
            fullWidth
            label="Status"
            value={filters.verificationStatus || filters.approvalStatus || ''}
            onChange={(v) => handleFilterChange('verificationStatus', v)}
            options={STATUS_OPTIONS}
          />

          {/* Component */}
          <SearchableSelect
            size="small"
            fullWidth
            label="Component"
            value={filters.component || ''}
            onChange={(v) => handleFilterChange('component', v)}
            options={[{ value: '', label: 'All Components' }, ...componentOptions.map((opt) => ({ value: opt.documentId, label: opt.displayName }))]}
          />

          {/* Section (independent - no parent) */}
          <SearchableSelect
            size="small"
            fullWidth
            label="Section"
            value={filters.section || ''}
            onChange={(v) => handleFilterChange('section', v)}
            options={[{ value: '', label: 'All Sections' }, ...sectionOptions.map((opt) => ({ value: opt.documentId, label: opt.displayName }))]}
          />

          {/* Categorization */}
          <SearchableSelect
            size="small"
            fullWidth
            label="Categorization"
            value={filters.classification || ''}
            onChange={(v) => handleFilterChange('classification', v)}
            options={[{ value: '', label: 'All Categorizations' }, ...classificationOptions.map((opt) => ({ value: opt.documentId, label: opt.displayName }))]}
          />

          {/* File Type (depends on Classification) */}
          <SearchableSelect
            size="small"
            fullWidth
            disabled={!filters.classification}
            label="File Type"
            value={filters.fileType || ''}
            onChange={(v) => handleFilterChange('fileType', v)}
            options={[{ value: '', label: 'All File Types' }, ...fileTypeOptions.map((opt) => ({ value: opt.documentId, label: opt.displayName }))]}
          />

          {/* Restricted */}
          <SearchableSelect
            size="small"
            fullWidth
            label="Restricted"
            value={filters.restricted || 'all'}
            onChange={(v) => handleFilterChange('restricted', v as any)}
            options={RESTRICTED_OPTIONS}
          />
        </Box>

        {/* Footer Actions */}
        <Box sx={{ pt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <BglCloseOutlinedSmallButton onClick={handleReset}>
            Reset Filters
          </BglCloseOutlinedSmallButton>
          <BglPrimarySmallButton onClick={handleApply}>
            Apply
          </BglPrimarySmallButton>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ConfigRegistryFiltersDrawer;

